// Music kept on this device so it plays without streaming - offline, or
// online without paying to serve it again. Copies live in the browser's Cache
// Storage (which the browser may clear when space runs low); which ones are
// saved, and for whom, is kept in localStorage.
//
// Plays made offline are queued here and sent to the server once the device
// is back online. Music the listener owns plays free; any other music is
// charged then (see music/offline_api.py), so offline it only plays while the
// balance last seen online covers it, and only within 30 days of checking in.
import { apiBaseURL } from '../constants/routes';
import { allowanceFrom, canPlayOffline as allowedOffline, spendAllowance } from '@tuneswell/shared/offlineRules';
import { storageSync } from './storage';

const CACHE_NAME = 'tuneswell-offline-v1';
const INDEX_KEY = 'tuneswell:offline-tracks';
const PLAYS_KEY = 'tuneswell:offline-plays';
const ALLOWANCE_KEY = 'tuneswell:offline-allowance';
export const OFFLINE_CHANGED = 'tuneswell:offline-changed';
// Progress of a copy being saved: detail {trackId, name, percent, done, error}
export const OFFLINE_PROGRESS = 'tuneswell:offline-progress';

const cacheKey = (trackId) => `/offline-audio/${trackId}`;

export const offlineSupported = () => typeof window !== 'undefined' && 'caches' in window;

const readJson = (key, fallback) => {
  try {
    return JSON.parse(localStorage.getItem(key)) || fallback;
  } catch (e) {
    return fallback;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Storage full or blocked - the copy itself is still cached
  }
};

const announce = () => window.dispatchEvent(new CustomEvent(OFFLINE_CHANGED));

// Saved tracks for this account (a shared device may hold several people's)
export const listSaved = (userId) => Object.values(readJson(INDEX_KEY, {}))
  .filter((entry) => entry.owner === userId)
  .sort((a, b) => (b.savedAt || '').localeCompare(a.savedAt || ''));

export const getSaved = (trackId, userId) => {
  const entry = readJson(INDEX_KEY, {})[trackId];
  return entry && entry.owner === userId ? entry : null;
};

// The balance and rate last seen online, for playing music the listener
// doesn't own while offline
const authHeader = () => ({ Authorization: `Token ${storageSync.getItem('token')}` });

const setAllowance = (userId, reply) => {
  const allowance = allowanceFrom(reply);
  if (!allowance) return;
  writeJson(ALLOWANCE_KEY, { ...readJson(ALLOWANCE_KEY, {}), [userId]: allowance });
};

export const refreshAllowance = async (userId) => {
  try {
    const response = await fetch(`${apiBaseURL}/music/offline-allowance/`, { headers: authHeader() });
    if (response.ok) setAllowance(userId, await response.json());
  } catch (e) {
    // offline - keep the last one
  }
};

// Whether a saved copy may play now with no connection (the rules are
// shared with the phone app: packages/shared/offlineRules.js)
export const canPlayOffline = (entry, userId) => allowedOffline(entry, readJson(ALLOWANCE_KEY, {})[userId]);

export const isSaved = (trackId, userId) => {
  const entry = readJson(INDEX_KEY, {})[trackId];
  return Boolean(entry && entry.owner === userId);
};

const progress = (detail) => window.dispatchEvent(new CustomEvent(OFFLINE_PROGRESS, { detail }));

export const saveTrack = async (track, userId) => {
  if (!offlineSupported()) throw new Error("This browser can't keep music offline");
  progress({ trackId: track.id, name: track.name, percent: 0 });
  let response;
  try {
    response = await fetch(`${apiBaseURL}/music/track/${track.id}/offline-file/`, { headers: authHeader() });
  } catch (e) {
    progress({ trackId: track.id, name: track.name, error: 'No connection' });
    throw new Error("You're offline - connect to keep music for offline listening");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const message = body.error || "Couldn't save this track for offline listening";
    progress({ trackId: track.id, name: track.name, error: message });
    throw new Error(message);
  }
  // Read it in pieces to report progress
  const total = Number(response.headers.get('Content-Length')) || 0;
  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    if (total) progress({ trackId: track.id, name: track.name, percent: Math.round((received / total) * 100) });
  }
  const blob = new Blob(chunks, { type: response.headers.get('Content-Type') || 'audio/mpeg' });
  const owned = response.headers.get('X-Tuneswell-Owned') === '1';
  const cache = await caches.open(CACHE_NAME);
  await cache.put(cacheKey(track.id), new Response(blob, {
    headers: { 'Content-Type': response.headers.get('Content-Type') || 'audio/mpeg' },
  }));

  const index = readJson(INDEX_KEY, {});
  index[track.id] = {
    id: track.id,
    name: track.name,
    act_name: track.act_name || track.acts || '',
    image: track.image || null,
    image_thumbnail_small: track.image_thumbnail_small || null,
    length: track.length || 0,
    slug: track.slug || null,
    size: blob.size,
    owned,
    owner: userId,
    savedAt: new Date().toISOString(),
  };
  writeJson(INDEX_KEY, index);
  if (!owned) await refreshAllowance(userId);
  progress({ trackId: track.id, name: track.name, percent: 100, done: true });
  announce();
};

export const removeTrack = async (trackId) => {
  if (offlineSupported()) {
    const cache = await caches.open(CACHE_NAME);
    await cache.delete(cacheKey(trackId));
  }
  const index = readJson(INDEX_KEY, {});
  delete index[trackId];
  writeJson(INDEX_KEY, index);
  announce();
};

// A playable URL for the saved copy, or null. The caller revokes it when done.
export const getOfflineUrl = async (trackId, userId) => {
  if (!offlineSupported() || !isSaved(trackId, userId)) return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const response = await cache.match(cacheKey(trackId));
    if (!response) {
      // The browser cleared it - forget it so the UI offers to save again
      await removeTrack(trackId);
      return null;
    }
    return URL.createObjectURL(await response.blob());
  } catch (e) {
    return null;
  }
};

export const recordOfflinePlay = (play, userId) => {
  if (!play.duration_seconds) return;
  writeJson(PLAYS_KEY, [...readJson(PLAYS_KEY, []), play]);
  // Music they don't own will be charged on sync - spend down the allowance now
  const entry = getSaved(play.track_id, userId);
  const allowances = readJson(ALLOWANCE_KEY, {});
  if (entry && !entry.owned && allowances[userId]) {
    allowances[userId] = spendAllowance(allowances[userId], play.duration_seconds);
    writeJson(ALLOWANCE_KEY, allowances);
  }
};

let syncing = false;

export const syncOfflinePlays = async (userId) => {
  const plays = readJson(PLAYS_KEY, []);
  const token = storageSync.getItem('token');
  if (syncing || !plays.length || !token || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
  syncing = true;
  try {
    const batch = plays.slice(0, 500);
    const response = await fetch(`${apiBaseURL}/music/offline-plays/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
      body: JSON.stringify({ plays: batch }),
    });
    if (response.ok) {
      // Keep anything queued while this request was in flight
      writeJson(PLAYS_KEY, readJson(PLAYS_KEY, []).slice(batch.length));
      if (userId) setAllowance(userId, await response.json().catch(() => ({})));
    }
  } catch (e) {
    // Still offline - try again later
  } finally {
    syncing = false;
  }
};
